import React, { useState, useEffect } from 'react'
import { PageLayout } from '../../components/layout/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { ToastContainer } from '../../components/ui/Toast'
import { useToast } from '../../hooks/useToast'
import { RemoteConfigService } from '../../services/api/remoteConfigService'
import { RemoteConfigValue } from '../../types/remoteConfig'
import { logger } from '../../utils/logger'
import { PencilIcon } from '@heroicons/react/24/outline'

export const LoginMessagePage: React.FC = () => {
  const [loginMessage, setLoginMessage] = useState<RemoteConfigValue | null>(null)
  const [messageText, setMessageText] = useState('')
  const [originalText, setOriginalText] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { toasts, showToast, removeToast } = useToast()

  useEffect(() => {
    loadLoginMessage()
  }, [])

  const loadLoginMessage = async () => {
    setLoading(true)
    try {
      const value = await RemoteConfigService.getValue('loginMessage')
      setLoginMessage(value)
      const text = value?.value || ''
      setMessageText(text)
      setOriginalText(text)
    } catch (error) {
      logger.error('Error loading login message:', error)
      showToast('Erreur lors du chargement du message', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (messageText === originalText) return

    setSaving(true)
    try {
      const updated = await RemoteConfigService.updateValue('loginMessage', messageText)
      setLoginMessage(updated)
      setOriginalText(messageText)
      showToast('Message de connexion mis à jour avec succès', 'success')
    } catch (error) {
      logger.error('Error saving login message:', error)
      showToast('Erreur lors de la sauvegarde du message', 'error')
    } finally {
      setSaving(false)
    }
  }

  const hasChanged = messageText !== originalText

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout maxWidth="2xl">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Message d&apos;accueil</h1>
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={!hasChanged || saving}
          className="flex items-center gap-2"
        >
          <PencilIcon className="h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Modifier'}
        </Button>
      </div>

      {/* Message Card */}
      <Card>
        <CardHeader>
          <CardTitle>Message de connexion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="login-message" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Message
              </label>
              <textarea
                id="login-message"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-white min-h-[120px] resize-y"
                placeholder="Entrez le message d&apos;accueil qui sera affiché sur la page de connexion..."
              />
            </div>

            {hasChanged && (
              <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <div className="w-2 h-2 bg-orange-600 dark:bg-orange-400 rounded-full"></div>
                Modifications non enregistrées
              </div>
            )}

            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p>Ce message sera affiché sur la page de connexion de l&apos;application mobile.</p>
              {loginMessage?.updatedAt && (
                <p className="mt-2">
                  Dernière modification : {new Date(loginMessage.updatedAt).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      {messageText && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Aperçu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {messageText}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </PageLayout>
  )
}